import React, { useState, useEffect } from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  RefreshCcw,
  BookOpen,
  Move,
  MapPin,
  Play,
  Trash2,
  Triangle,
  MousePointer2,
  Box,
  PieChart,
  Eye,
  EyeOff,
  RotateCw,
} from "lucide-react";

const CentralSymmetryLab = () => {
  // 核心状态：领航员A的当前位置
  const [posA, setPosA] = useState({ x: 0, y: 0 });

  // 模式：'explore' (自由探索/位移), 'teach' (翻折原理教学)
  const [mode, setMode] = useState("explore");

  // 教学模式下的步骤: 0=初始, 1=Y轴翻折, 2=X轴翻折, 3=直接旋转动画
  const [teachStep, setTeachStep] = useState(0);

  // 专门用于控制旋转动画的角度状态
  const [animRotation, setAnimRotation] = useState(0);

  // 当前选择的图形类型
  const [shapeType, setShapeType] = useState("kite");

  // 是否显示坐标标签
  const [showLabels, setShowLabels] = useState(true);

  // 关键点历史 (路径系统)
  const [waypoints, setWaypoints] = useState([{ x: 0, y: 0, id: 0 }]);

  // 坐标系缩放
  const GRID_SIZE = 30;
  const AXIS_RANGE = 10;

  // 基础旋转角度
  const BASE_ROTATION = 15;

  // 监听步骤变化，触发旋转动画
  useEffect(() => {
    if (mode === "teach" && teachStep === 3) {
      // 1. 先重置为 0
      setAnimRotation(0);

      // 2. 使用 requestAnimationFrame 确保在下一帧触发动画
      // 这里的 setTimeout 也是为了确保浏览器有时间先渲染 0 度的状态
      const timer = setTimeout(() => {
        setAnimRotation(180);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      // 离开步骤3时重置
      setAnimRotation(0);
    }
  }, [mode, teachStep]);

  // 预设颜色
  const SEGMENT_COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#06b6d4",
  ];

  // 图形库定义 (SVG path) - 100x100 居中
  const SHAPE_PATHS = {
    kite: (
      <>
        <path d="M50 20 L80 50 L50 90 L20 50 Z" />
        <circle cx="50" cy="40" r="5" fill="white" fillOpacity="0.7" />
        <path d="M50 20 L50 60" stroke="white" strokeWidth="2" />
      </>
    ),
    l_shape: (
      <>
        {/* 竖条: x=30~50, y=10~70; 横条: x=50~90, y=50~70 */}
        <path d="M30 10 L50 10 L50 50 L90 50 L90 70 L30 70 Z" />
        {/* 圆点放在 (50, 50)，正好是网格中心 */}
        <circle
          cx="50"
          cy="50"
          r="4"
          fill="white"
          stroke="currentColor"
          strokeWidth="2"
        />
      </>
    ),
    arrow: (
      <>
        <path d="M20 50 L60 20 L60 40 L90 40 L90 60 L60 60 L60 80 Z" />
        <circle cx="60" cy="50" r="4" fill="white" />
      </>
    ),
    pacman: (
      <>
        <path d="M50 50 L85 25 A40 40 0 1 1 85 75 Z" />
        <circle cx="50" cy="30" r="5" fill="white" />
      </>
    ),
  };

  const SHAPE_ICONS = {
    kite: <Triangle className="w-4 h-4" />,
    l_shape: <Box className="w-4 h-4" />,
    arrow: <MousePointer2 className="w-4 h-4 rotate-90" />,
    pacman: <PieChart className="w-4 h-4" />,
  };

  const SHAPE_LABELS = {
    kite: "风筝",
    l_shape: "L形",
    arrow: "箭头",
    pacman: "吃豆人",
  };

  // 移动领航员A
  const moveA = (dx, dy) => {
    setPosA((prev) => ({
      x: Math.max(-AXIS_RANGE, Math.min(AXIS_RANGE, prev.x + dx)),
      y: Math.max(-AXIS_RANGE, Math.min(AXIS_RANGE, prev.y + dy)),
    }));
  };

  // 确认当前位置
  const confirmStep = () => {
    const lastPoint = waypoints[waypoints.length - 1];
    if (lastPoint.x === posA.x && lastPoint.y === posA.y) return;
    setWaypoints((prev) => [...prev, { ...posA, id: Date.now() }]);
  };

  // 重置
  const reset = () => {
    const startPos = { x: 0, y: 0 };
    setPosA(startPos);
    setWaypoints([{ ...startPos, id: 0 }]);
    setTeachStep(0);
  };

  // 切换模式逻辑
  const switchMode = (newMode) => {
    setMode(newMode);
    if (newMode === "teach") {
      setTeachStep(0);
    }
  };

  // 坐标转换
  const toScreen = (x, y) => ({
    x: x * GRID_SIZE,
    y: -y * GRID_SIZE,
  });

  // 渲染器组件
  const ShapeRenderer = ({
    type,
    color,
    rotation,
    opacity = 1,
    isGhost = false,
    scaleX = 1,
    scaleY = 1,
  }) => (
    <svg
      width="40"
      height="40"
      viewBox="0 0 100 100"
      className={`transition-all duration-700 ease-in-out drop-shadow-md`}
      style={{
        transform: `rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`,
        opacity: opacity,
        filter: isGhost ? "grayscale(100%) opacity(60%)" : "none",
      }}
    >
      <g
        fill="currentColor"
        className={color}
        stroke="white"
        strokeWidth="3"
        strokeLinejoin="round"
      >
        {SHAPE_PATHS[type] || SHAPE_PATHS["kite"]}
      </g>
    </svg>
  );

  const Entity = ({
    x,
    y,
    colorClass,
    label,
    opacity = 1,
    rotation = 0,
    isGhost = false,
    scaleX = 1,
    scaleY = 1,
    customStyle = {},
  }) => {
    const screenPos = toScreen(x, y);
    return (
      <div
        className={`absolute flex flex-col items-center justify-center transition-all duration-700 ease-in-out z-30`}
        style={{
          left: `calc(50% + ${screenPos.x}px)`,
          top: `calc(50% + ${screenPos.y}px)`,
          transform: `translate(-50%, -50%)`,
          opacity: opacity,
          ...customStyle,
        }}
      >
        <ShapeRenderer
          type={shapeType}
          color={colorClass}
          rotation={rotation}
          scaleX={scaleX}
          scaleY={scaleY}
          opacity={opacity}
          isGhost={isGhost}
        />

        {/* 坐标气泡 */}
        {showLabels && (
          <div
            className={`absolute left-full top-0 ml-1 flex items-center transition-opacity duration-300 pointer-events-none z-40
              ${isGhost ? "opacity-60" : "opacity-100"}`}
          >
            {/* 小三角指向图形 */}
            <div
              className={`w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-r-[6px] 
               ${isGhost ? "border-r-slate-200" : "border-r-slate-800"}`}
            ></div>

            <div
              className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold whitespace-nowrap
              ${
                isGhost
                  ? "bg-slate-100 text-slate-400 border border-slate-200"
                  : "bg-slate-800/80 text-white backdrop-blur-sm"
              }`}
            >
              {label} ({x}, {y})
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染路径
  const renderPath = (
    points,
    transformFn = (p) => p,
    type = "normal",
    containerRotation = 0
  ) => {
    return (
      <g
        style={{
          transform: `rotate(${containerRotation}deg)`,
          transformOrigin: "50% 50%",
          // 这里的动画参数是 standard css syntax
          transition: "transform 1.5s ease-in-out",
        }}
      >
        {points.map((pt, i) => {
          if (i === 0) return null;
          const prev = points[i - 1];

          const p1 = transformFn(prev);
          const p2 = transformFn(pt);

          const start = toScreen(p1.x, p1.y);
          const end = toScreen(p2.x, p2.y);

          const color =
            type === "ghost"
              ? "#cbd5e1"
              : SEGMENT_COLORS[(i - 1) % SEGMENT_COLORS.length];

          const dashArray = type === "ghost" ? "4,4" : "0";

          return (
            <g key={`${type}-path-${i}`}>
              <line
                x1={`calc(50% + ${start.x}px)`}
                y1={`calc(50% + ${start.y}px)`}
                x2={`calc(50% + ${end.x}px)`}
                y2={`calc(50% + ${end.y}px)`}
                stroke={color}
                strokeWidth={type === "ghost" ? 2 : 4}
                opacity={type === "ghost" ? 0.6 : 0.8}
                strokeLinecap="round"
                strokeDasharray={dashArray}
              />
              {i < points.length && (
                <circle
                  cx={`calc(50% + ${end.x}px)`}
                  cy={`calc(50% + ${end.y}px)`}
                  r={type === "ghost" ? 2 : 4}
                  fill="white"
                  stroke={color}
                  strokeWidth="2"
                />
              )}
            </g>
          );
        })}
      </g>
    );
  };

  const renderPreviewLine = (isMirror = false) => {
    const lastConfirmed = waypoints[waypoints.length - 1];
    if (lastConfirmed.x === posA.x && lastConfirmed.y === posA.y) return null;
    const p1 = isMirror
      ? { x: -lastConfirmed.x, y: -lastConfirmed.y }
      : lastConfirmed;
    const p2 = isMirror ? { x: -posA.x, y: -posA.y } : posA;
    const start = toScreen(p1.x, p1.y);
    const end = toScreen(p2.x, p2.y);

    return (
      <line
        x1={`calc(50% + ${start.x}px)`}
        y1={`calc(50% + ${start.y}px)`}
        x2={`calc(50% + ${end.x}px)`}
        y2={`calc(50% + ${end.y}px)`}
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="4,4"
        className="animate-pulse"
      />
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-slate-50 overflow-hidden font-sans select-none">
      {/* 左侧控制面板 */}
      <div className="w-full md:w-80 bg-white shadow-xl z-20 flex flex-col p-5 border-r border-slate-200 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <RefreshCcw className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">中心对称探索</h1>
            <p className="text-xs text-slate-500">
              双星领航员 v4.2 Pro (Sync Fix)
            </p>
          </div>
        </div>

        {/* 模式切换 */}
        <div className="flex bg-slate-100 p-1 rounded-lg mb-4 shrink-0">
          <button
            onClick={() => switchMode("explore")}
            className={`flex-1 py-1.5 text-xs font-medium rounded flex items-center justify-center gap-1.5 transition-all ${
              mode === "explore"
                ? "bg-white shadow text-indigo-600"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Move className="w-3 h-3" /> 路径探索
          </button>
          <button
            onClick={() => switchMode("teach")}
            className={`flex-1 py-1.5 text-xs font-medium rounded flex items-center justify-center gap-1.5 transition-all ${
              mode === "teach"
                ? "bg-white shadow text-pink-600"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <BookOpen className="w-3 h-3" /> 翻折原理
          </button>
        </div>

        {/* 图形选择器 & 标签开关 */}
        <div className="mb-6 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              选择形状
            </h3>
            <button
              onClick={() => setShowLabels(!showLabels)}
              className="text-[10px] flex items-center gap-1 text-slate-400 hover:text-indigo-600 transition-colors"
              title={showLabels ? "隐藏坐标标签" : "显示坐标标签"}
            >
              {showLabels ? (
                <Eye className="w-3 h-3" />
              ) : (
                <EyeOff className="w-3 h-3" />
              )}
              {showLabels ? "隐藏坐标" : "显示坐标"}
            </button>
          </div>

          <div className="flex gap-2">
            {["kite", "l_shape", "arrow", "pacman"].map((type) => (
              <button
                key={type}
                onClick={() => setShapeType(type)}
                title={SHAPE_LABELS[type]}
                className={`flex-1 aspect-square rounded-lg flex items-center justify-center transition-all border
                    ${
                      shapeType === type
                        ? "bg-indigo-50 border-indigo-300 text-indigo-600 shadow-sm"
                        : "bg-white border-slate-200 text-slate-400 hover:bg-slate-50"
                    }`}
              >
                <div style={{ transform: "rotate(15deg)" }}>
                  {SHAPE_ICONS[type]}
                </div>
              </button>
            ))}
          </div>
        </div>

        {mode === "explore" ? (
          <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-500">
            {/* 1. 当前坐标控制 */}
            <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-200/30 rounded-full blur-xl -mr-4 -mt-4 pointer-events-none"></div>

              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-bold text-indigo-500 uppercase tracking-wider flex items-center gap-1">
                  <Move className="w-3 h-3" /> 调整目标点 A
                </h3>
                <button
                  onClick={reset}
                  className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> 重置
                </button>
              </div>

              {/* 方向键 */}
              <div className="grid grid-cols-3 gap-1.5 max-w-[140px] mx-auto mb-4">
                <div></div>
                <button
                  onClick={() => moveA(0, 1)}
                  className="aspect-square bg-white hover:bg-indigo-600 hover:text-white text-indigo-600 rounded-lg shadow-sm border border-indigo-100 transition-colors flex items-center justify-center active:scale-95"
                >
                  <ArrowUp size={18} />
                </button>
                <div></div>
                <button
                  onClick={() => moveA(-1, 0)}
                  className="aspect-square bg-white hover:bg-indigo-600 hover:text-white text-indigo-600 rounded-lg shadow-sm border border-indigo-100 transition-colors flex items-center justify-center active:scale-95"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="flex items-center justify-center font-bold text-indigo-900/30 text-xs">
                  A
                </div>
                <button
                  onClick={() => moveA(1, 0)}
                  className="aspect-square bg-white hover:bg-indigo-600 hover:text-white text-indigo-600 rounded-lg shadow-sm border border-indigo-100 transition-colors flex items-center justify-center active:scale-95"
                >
                  <ArrowRight size={18} />
                </button>
                <div></div>
                <button
                  onClick={() => moveA(0, -1)}
                  className="aspect-square bg-white hover:bg-indigo-600 hover:text-white text-indigo-600 rounded-lg shadow-sm border border-indigo-100 transition-colors flex items-center justify-center active:scale-95"
                >
                  <ArrowDown size={18} />
                </button>
                <div></div>
              </div>

              <div className="flex gap-2 text-sm font-mono justify-center mb-3">
                <div className="bg-white px-2 py-1 rounded border border-indigo-100 text-indigo-700">
                  X: {posA.x}
                </div>
                <div className="bg-white px-2 py-1 rounded border border-indigo-100 text-indigo-700">
                  Y: {posA.y}
                </div>
              </div>

              <button
                onClick={confirmStep}
                disabled={
                  waypoints[waypoints.length - 1].x === posA.x &&
                  waypoints[waypoints.length - 1].y === posA.y
                }
                className={`w-full py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-sm
                  ${
                    waypoints[waypoints.length - 1].x === posA.x &&
                    waypoints[waypoints.length - 1].y === posA.y
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white active:scale-95"
                  }`}
              >
                <MapPin className="w-4 h-4" /> 确认落子 (记录本步)
              </button>
            </div>

            {/* 2. 历史路径列表 */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 min-h-[120px]">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>{" "}
                移动记录本
              </h3>
              <div className="space-y-2">
                {waypoints.map((pt, idx) => (
                  <div
                    key={pt.id}
                    className="flex items-center text-xs text-slate-600 bg-white p-2 rounded border border-slate-100 shadow-sm relative overflow-hidden"
                  >
                    <div
                      className="w-1 h-full absolute left-0 top-0 bottom-0"
                      style={{
                        backgroundColor:
                          idx === 0
                            ? "#94a3b8"
                            : SEGMENT_COLORS[(idx - 1) % SEGMENT_COLORS.length],
                      }}
                    ></div>
                    <span className="ml-2 font-mono w-6 text-slate-400">
                      #{idx}
                    </span>
                    <span className="font-bold flex-1">
                      {idx === 0 ? "起点" : "移动到"} ({pt.x}, {pt.y})
                    </span>
                  </div>
                ))}
                {(waypoints[waypoints.length - 1].x !== posA.x ||
                  waypoints[waypoints.length - 1].y !== posA.y) && (
                  <div className="flex items-center text-xs text-indigo-500 bg-indigo-50/50 p-2 rounded border border-dashed border-indigo-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse ml-1 mr-2"></div>
                    <span className="italic">
                      正在规划... ({posA.x}, {posA.y})
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-500">
            <div className="bg-pink-50 p-4 rounded-xl border border-pink-100">
              <h3 className="font-bold text-pink-800 mb-2">两种理解方式</h3>

              {posA.x === 0 && posA.y === 0 && waypoints.length <= 1 ? (
                <div className="text-sm text-pink-600 bg-pink-100 p-3 rounded mb-4">
                  ⚠️ 还没有移动轨迹。建议先去移动几步。
                </div>
              ) : (
                <p className="text-xs text-pink-700 mb-4 leading-relaxed">
                  选择下方按钮，观察“中心对称”的两种动画过程。
                </p>
              )}

              <div className="space-y-2">
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 mb-2">
                  方法一：两次翻折 (轴对称组合)
                </h4>
                {[
                  { title: "1. 初始状态", desc: `准备出发` },
                  {
                    title: "2. 左右翻折 (Y轴)",
                    desc: `沿 Y 轴像翻书一样翻过去`,
                  },
                  {
                    title: "3. 上下翻折 (X轴)",
                    desc: `沿 X 轴向下翻折，到达终点`,
                  },
                ].map((step, i) => (
                  <button
                    key={i}
                    onClick={() => setTeachStep(i)}
                    className={`w-full p-2.5 text-left rounded-lg transition-all border flex justify-between items-center ${
                      teachStep === i
                        ? "bg-white border-pink-500 shadow-md ring-1 ring-pink-100"
                        : "bg-transparent border-transparent hover:bg-white/50"
                    }`}
                  >
                    <div>
                      <span
                        className={`font-bold block text-sm ${
                          teachStep === i ? "text-pink-700" : "text-slate-600"
                        }`}
                      >
                        {step.title}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {step.desc}
                      </span>
                    </div>
                    {teachStep === i && (
                      <Play className="w-4 h-4 text-pink-500 fill-current" />
                    )}
                  </button>
                ))}

                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-4 mb-2">
                  方法二：直接旋转 (中心对称定义)
                </h4>
                <button
                  onClick={() => setTeachStep(3)}
                  className={`w-full p-2.5 text-left rounded-lg transition-all border flex justify-between items-center ${
                    teachStep === 3
                      ? "bg-white border-indigo-500 shadow-md ring-1 ring-indigo-100"
                      : "bg-indigo-50/50 border-transparent hover:bg-white/50"
                  }`}
                >
                  <div>
                    <span
                      className={`font-bold block text-sm ${
                        teachStep === 3 ? "text-indigo-700" : "text-indigo-600"
                      }`}
                    >
                      4. 旋转 180°
                    </span>
                    <span className="text-[10px] text-indigo-400">
                      绕原点直接旋转半圈 (点击重播)
                    </span>
                  </div>
                  {teachStep === 3 && (
                    <RotateCw className="w-4 h-4 text-indigo-500 animate-spin-slow" />
                  )}
                </button>
              </div>
            </div>

            {teachStep === 3 && (
              <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100 text-xs text-indigo-700 animate-in fade-in zoom-in duration-300">
                <span className="font-bold block mb-1">
                  这就是中心对称的本质：
                </span>
                整个图形绕着原点 O 旋转 180
                度。你会发现，旋转后的位置和“翻折两次”的位置是完全一样的！
              </div>
            )}

            {teachStep === 2 && (
              <div className="p-3 bg-pink-50 rounded-xl border border-pink-100 text-xs text-pink-700 animate-in fade-in zoom-in duration-300">
                <span className="font-bold block mb-1">翻折结论：</span>
                左右翻(-x, y) + 上下翻(-x, -y) = 旋转180度。
              </div>
            )}
          </div>
        )}
      </div>

      {/* 右侧主绘图区 */}
      <div className="flex-1 relative bg-white/50 overflow-hidden cursor-crosshair">
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <div
            className="relative"
            style={{
              width: GRID_SIZE * AXIS_RANGE * 2,
              height: GRID_SIZE * AXIS_RANGE * 2,
            }}
          >
            {/* 1. 基础网格 */}
            {(() => {
              const lines = [];
              for (let i = -AXIS_RANGE; i <= AXIS_RANGE; i++) {
                lines.push(
                  <div
                    key={`v-${i}`}
                    className={`absolute top-0 bottom-0 w-px ${
                      i === 0 ? "bg-slate-800 w-0.5 z-10" : "bg-slate-200"
                    }`}
                    style={{ left: `calc(50% + ${i * GRID_SIZE}px)` }}
                  />,
                  <div
                    key={`h-${i}`}
                    className={`absolute left-0 right-0 h-px ${
                      i === 0 ? "bg-slate-800 h-0.5 z-10" : "bg-slate-200"
                    }`}
                    style={{ top: `calc(50% + ${i * GRID_SIZE}px)` }}
                  />
                );
              }
              return lines;
            })()}

            {/* 原点O */}
            <div className="absolute left-1/2 top-1/2 w-4 h-4 bg-white border-4 border-slate-800 rounded-full -translate-x-1/2 -translate-y-1/2 z-20 shadow-sm"></div>

            {/* 2. SVG层：路径绘制 */}
            <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none overflow-visible">
              {/* (A) A与B的实时对称连线 (仅在探索或教学完成时) */}
              {(mode === "explore" || teachStep === 2 || teachStep === 3) && (
                <line
                  x1={`calc(50% + ${toScreen(posA.x, posA.y).x}px)`}
                  y1={`calc(50% + ${toScreen(posA.x, posA.y).y}px)`}
                  x2={`calc(50% + ${toScreen(-posA.x, -posA.y).x}px)`}
                  y2={`calc(50% + ${toScreen(-posA.x, -posA.y).y}px)`}
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeDasharray="4,4"
                  opacity="0.5"
                />
              )}

              {/* (B) 路径渲染逻辑 */}

              {/* 始终显示 A 的路径 */}
              {renderPath(waypoints, (p) => p, "normal")}

              {/* 探索模式 或 教学完成(步骤2/3)：显示 B (最终对称路径) */}
              {
                (mode === "explore" || teachStep === 2 || teachStep === 3) &&
                  renderPath(waypoints, (p) => ({ x: -p.x, y: -p.y }), "normal") // B: (-x, -y)
              }

              {/* 教学动画：翻折路径演示 
                 Step 1: 仅翻折Y (Ghost Path)
              */}
              {mode === "teach" &&
                teachStep >= 1 &&
                teachStep < 3 &&
                renderPath(waypoints, (p) => ({ x: -p.x, y: p.y }), "ghost")}

              {/* 教学动画：旋转路径演示 (Step 3) 
                  使用 animRotation 状态来控制旋转角度
              */}
              {mode === "teach" &&
                teachStep === 3 &&
                renderPath(waypoints, (p) => p, "ghost", animRotation)}

              {/* (C) 预览线 */}
              {mode === "explore" && renderPreviewLine(false)}
              {mode === "explore" && renderPreviewLine(true)}
            </svg>

            {/* 3. 实体层 */}

            {/* A: 原始点 */}
            <Entity
              x={posA.x}
              y={posA.y}
              colorClass="text-indigo-500 fill-indigo-500"
              label="A"
              rotation={BASE_ROTATION}
            />

            {/* B: 最终对称点 */}
            <Entity
              x={-posA.x}
              y={-posA.y}
              colorClass="text-rose-500 fill-rose-500"
              label="B"
              rotation={BASE_ROTATION + 180}
              // 在 Step 3 旋转动画时，B 会在动画结束时无缝衔接
              opacity={
                mode === "explore" || teachStep === 2 || teachStep === 3 ? 1 : 0
              }
            />

            {/* 教学用幽灵：翻折动画 (Step 0 -> 1 -> 2)
                这里使用 CSS transition 来模拟翻折的“挤压”效果
            */}
            {mode === "teach" && teachStep < 3 && (
              <Entity
                // Step 0: 在 A 处
                // Step 1: 在 (-x, y) 处
                // Step 2: 在 (-x, -y) 处
                x={teachStep === 0 ? posA.x : -posA.x}
                y={teachStep === 2 ? -posA.y : posA.y}
                colorClass="text-slate-400 fill-slate-400"
                label="Ghost"
                rotation={BASE_ROTATION}
                // Step 1: 水平翻转 (-1, 1)
                // Step 2: 水平+垂直翻转 (-1, -1)
                scaleX={teachStep >= 1 ? -1 : 1}
                scaleY={teachStep === 2 ? -1 : 1}
                isGhost={true}
                opacity={
                  teachStep === 0
                    ? 0
                    : teachStep === 1 || teachStep === 2
                    ? 0.8
                    : 0
                }
              />
            )}

            {/* 教学用幽灵：旋转动画 (Step 3) - 已修复同步问题 */}
            {mode === "teach" && teachStep === 3 && (
              <div
                className="absolute left-1/2 top-1/2 w-0 h-0 z-30"
                style={{
                  transform: `rotate(${animRotation}deg)`,
                  // 关键修改：手动指定 transition 以匹配 SVG 的动画曲线 (ease-in-out)
                  transition: "transform 1.5s ease-in-out",
                }}
              >
                {/* 这里的 Entity 坐标相对于旋转容器 (0,0) */}
                <div
                  style={{
                    position: "absolute",
                    left: `${toScreen(posA.x, posA.y).x}px`,
                    top: `${toScreen(posA.x, posA.y).y}px`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <ShapeRenderer
                    type={shapeType}
                    color="text-slate-400 fill-slate-400"
                    rotation={BASE_ROTATION}
                    opacity={0.6}
                    isGhost={true}
                  />
                </div>
              </div>
            )}

            {mode === "teach" && teachStep === 1 && (
              <div className="absolute top-0 bottom-0 w-1 bg-yellow-400/30 left-1/2 -translate-x-1/2 animate-pulse"></div>
            )}
            {mode === "teach" && teachStep === 2 && (
              <div className="absolute left-0 right-0 h-1 bg-yellow-400/30 top-1/2 -translate-y-1/2 animate-pulse"></div>
            )}
          </div>
        </div>

        {/* 右上角提示 */}
        <div className="absolute top-4 right-4 pointer-events-none">
          <div className="bg-white/90 backdrop-blur border border-slate-200 shadow-sm rounded-lg p-3 text-xs max-w-[200px]">
            <p className="font-bold text-slate-700 mb-1">操作指南：</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li>
                移动 <span className="text-indigo-600 font-bold">蓝色A</span>{" "}
                调整位置
              </li>
              <li>点击“确认落子”记录轨迹</li>
              <li>点击左侧“翻折原理”查看动画</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CentralSymmetryLab;
